import React, { useEffect, useState, useMemo } from "react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Header from "./Header";
import { useNavigate } from "react-router-dom";

import MedChatImg from "../assets/MedChat.png";
import PlatformImg from "../assets/Platform.png";
import CodabenchImg from "../assets/Codabench.png";

import Ad1 from "../assets/Ad1.png";
import Ad2 from "../assets/Ad2.png";
import Ad3 from "../assets/Ad3.png";
import NewsBackground from "../assets/news_background.jpg";
import "./NewsRoom.css";

/** Latest News cards (titles + links updated per request) */
const latestNews = [
  {
    date: "August 18, 2025",
    title: "SynthGuard Web Platform Launched",
    image: PlatformImg,
    actions: [{ label: "Read Blog →", href: "#" }],
  },
  {
    date: "August 18, 2025",
    title: "Join the Codabench Competition",
    image: CodabenchImg,
    actions: [
      {
        label: "Application",
        href:
          "https://docs.google.com/forms/d/e/1FAIpQLSeaaEIo3zCiNVeVjIkn-v54sj8-ctAjOO_94lK4xPFFQD_KVQ/viewform",
      },
      {
        label: "Info Page",
        href: "https://sites.google.com/view/aifacedetection/home",
      },
      {
        label: "Competition",
        href: "https://www.codabench.org/competitions/7166/",
      },
    ],
  },
  {
    date: "July 30, 2025",
    title: "(Try MedChat)",
    image: MedChatImg,
    actions: [
      { label: "Launch MedChat", href: "https://in-engr-nova.it.purdue.edu/MedChat/" },
    ],
  },
];

function Footer() {
  return (
    <footer className="nr-footer">
      <div>© 2025 Purdue University</div>
      <div>Developed by Machine Learning and Media Forensics (M2) Lab</div>
      <div className="nr-footer-links">
        <a href="#">Privacy Policy</a> | <a href="#">Terms of Use</a> | <a href="#">Contact</a>
      </div>
    </footer>
  );
}

export default function NewsRoom() {
  const [loading, setLoading] = useState(true);
  const [activeLink, setActiveLink] = useState("Newsroom");
  const [slide, setSlide] = useState(0);
  const navigate = useNavigate();

  // slideshow slides
  const slides = useMemo(
    () => [
      {
        img: Ad1,
        title: "SynthGuard Web Platform Launched",
        desc: "AI-powered deepfake detection for protecting digital content at scale.",
        cta: "#",
        wrapLink: null,
      },
      {
        img: Ad2,
        title: "MedChat, the ultimate LLM",
        desc: "Secure, clinician-friendly conversations powered by state-of-the-art models.",
        cta: "https://in-engr-nova.it.purdue.edu/MedChat/",
        wrapLink: "https://in-engr-nova.it.purdue.edu/MedChat/",
      },
      {
        img: Ad3,
        title: "Tell us what you think",
        desc: "Quick form — share feedback and interest.",
        cta:
          "https://docs.google.com/forms/d/e/1FAIpQLSeaaEIo3zCiNVeVjIkn-v54sj8-ctAjOO_94lK4xPFFQD_KVQ/viewform",
        wrapLink:
          "https://docs.google.com/forms/d/e/1FAIpQLSeaaEIo3zCiNVeVjIkn-v54sj8-ctAjOO_94lK4xPFFQD_KVQ/viewform",
      },
    ],
    []
  );

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(t);
  }, []);

  // auto-advance slideshow
  useEffect(() => {
    if (loading) return;
    const id = setInterval(() => setSlide((s) => (s + 1) % slides.length), 9000);
    return () => clearInterval(id);
  }, [loading, slides.length]);

  const handleNavClick = (item) => {
    setActiveLink(item);
    if (item === "Home") navigate("/");
    else if (item === "Team") navigate("/team");
    else if (item === "Feedback") navigate("/feedback");
    else if (item === "Statistics") navigate("/statistics");
    else if (item === "Interviews") navigate("/interview");
    else navigate(`/${item.toLowerCase()}`);
  };

  return (
    <div
      className="nr-wrap"
      style={{
        backgroundImage: `url(${NewsBackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <Header activeLink={activeLink} onNavClick={handleNavClick} />

      <div style={{ height: 24 }} />

      <SkeletonTheme baseColor="#202225" highlightColor="#3a3f47" duration={1.1}>
        {/* HERO / SLIDESHOW */}
        <section className="nr-hero">
          {loading ? (
            <div className="nr-hero-skel">
              <Skeleton height={380} borderRadius={16} />
              <div className="nr-hero-overlay">
                <Skeleton width={340} height={28} />
                <Skeleton width={520} height={18} style={{ marginTop: 10 }} />
                <Skeleton width={120} height={36} style={{ marginTop: 14, borderRadius: 8 }} />
              </div>
            </div>
          ) : (
            <div className="nr-hero-slider">
              <div className="nr-hero-track" style={{ transform: `translateX(-${slide * 100}%)` }}>
                {slides.map((s, i) => {
                  const SlideInner = (
                    <>
                      <img src={s.img} alt={s.title} loading="lazy" draggable={false} />
                      <div className="nr-hero-overlay">
                        <h1>{s.title}</h1>
                        <p>{s.desc}</p>
                        <a className="nr-cta" href={s.cta} target="_blank" rel="noreferrer noopener">
                          Read More
                        </a>
                      </div>
                    </>
                  );

                  return s.wrapLink ? (
                    <a
                      key={i}
                      className="nr-hero-slide"
                      href={s.wrapLink}
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label={s.title}
                    >
                      {SlideInner}
                    </a>
                  ) : (
                    <div className="nr-hero-slide" key={i}>
                      {SlideInner}
                    </div>
                  );
                })}
              </div>

              {/* dots */}
              <div className="nr-dots">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    className={`nr-dot ${i === slide ? "active" : ""}`}
                    aria-label={`Go to slide ${i + 1}`}
                    onClick={() => setSlide(i)}
                  />
                ))}
              </div>
            </div>
          )}
        </section>

        {/* LATEST NEWS */}
        <main className="nr-main">
          <h2 className="nr-section-title">Latest News</h2>

          {loading ? (
            <div className="nr-grid">
              {[1, 2, 3].map((i) => (
                <article key={i} className="nr-card">
                  <Skeleton height={220} borderRadius="12px 12px 0 0" />
                  <div className="nr-card-body">
                    <Skeleton width={140} height={12} />
                    <Skeleton height={18} style={{ marginTop: 8 }} />
                    <Skeleton height={18} width="80%" style={{ marginTop: 6 }} />
                    <Skeleton width={84} height={14} style={{ marginTop: 10 }} />
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="nr-grid">
              {latestNews.map((n, idx) => (
                <div key={idx} className="nr-card">
                  <div className="nr-thumb">
                    <img src={n.image} alt={n.title} loading="lazy" draggable={false} />
                  </div>
                  <div className="nr-card-body">
                    <div className="nr-date">{n.date}</div>
                    <h3 className="nr-card-title">{n.title}</h3>

                    {n.actions?.length ? (
                      <div className="nr-cta-row">
                        {n.actions.map((a, i) => (
                          <a
                            key={i}
                            className="nr-cta-link"
                            href={a.href}
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            {a.label}
                          </a>
                        ))}

                        {/* 🆕 Special glowing Purdue-only message for MedChat */}
                        {n.title.includes("MedChat") && (
                          <div className="vpn-warning-btn">
                            Only for Purdue Internal Users – Requires Purdue VPN
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="nr-read">Read Blog →</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </SkeletonTheme>

      <Footer />
    </div>
  );
}
